import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  CircularProgress,
  Autocomplete,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import PageBackHeader from '../components/PageBackHeader';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import NotesIcon from '@mui/icons-material/Notes';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import {
  supplierDirectoryService,
  rawMaterialService,
  dyeService,
  packagingMaterialService,
  filamentService,
  consumableMaterialService,
} from '../services/api';

function SectionHeader({ icon, title }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
  );
}

const fieldRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2,
};

const MATERIAL_TABS = ['Усі', 'Сировина', 'Барвники', 'Пакувальні матеріали', 'Філамент', 'Розхідні'];

function SupplierEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [matTab, setMatTab] = useState(0);

  const [form, setForm] = useState({
    name: '',
    contact_name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    is_active: true,
  });

  const [selectedRawMaterials, setSelectedRawMaterials] = useState([]);
  const [selectedDyes, setSelectedDyes] = useState([]);
  const [selectedPackaging, setSelectedPackaging] = useState([]);
  const [selectedFilaments, setSelectedFilaments] = useState([]);
  const [selectedConsumables, setSelectedConsumables] = useState([]);
  const [allRawMaterials, setAllRawMaterials] = useState([]);
  const [allDyes, setAllDyes] = useState([]);
  const [allPackaging, setAllPackaging] = useState([]);
  const [allFilaments, setAllFilaments] = useState([]);
  const [allConsumables, setAllConsumables] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [rm, dy, pk, fl, cm] = await Promise.all([
          rawMaterialService.getAll(),
          dyeService.getAll(),
          packagingMaterialService.getAll(),
          filamentService.getAll(),
          consumableMaterialService.getAll(),
        ]);
        setAllRawMaterials(rm.data.results || rm.data || []);
        setAllDyes(dy.data.results || dy.data || []);
        setAllPackaging(pk.data.results || pk.data || []);
        setAllFilaments(fl.data.results || fl.data || []);
        setAllConsumables(cm.data.results || cm.data || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const loadOne = useCallback(async () => {
    const res = await supplierDirectoryService.getById(id);
    const s = res.data;
    setForm({
      name: s.name || '',
      contact_name: s.contact_name || '',
      phone: s.phone || '',
      email: s.email || '',
      address: s.address || '',
      notes: s.notes || '',
      is_active: s.is_active !== false,
    });
    // Store raw IDs temporarily; resolved to objects once all* arrays load
    if (Array.isArray(s.raw_materials)) setSelectedRawMaterials(s.raw_materials);
    if (Array.isArray(s.dyes)) setSelectedDyes(s.dyes);
    if (Array.isArray(s.packaging_materials)) setSelectedPackaging(s.packaging_materials);
    if (Array.isArray(s.filaments)) setSelectedFilaments(s.filaments);
    if (Array.isArray(s.consumable_materials)) setSelectedConsumables(s.consumable_materials);
  }, [id]);

  // Resolve numeric IDs -> full objects once the lookup arrays are populated
  const resolveIds = useCallback((ids, all) => {
    if (!ids.length || !all.length) return ids;
    if (typeof ids[0] === 'object') return ids; // already objects
    return ids.map((id) => all.find((o) => o.id === id || o.id === Number(id))).filter(Boolean);
  }, []);

  useEffect(() => {
    if (!allRawMaterials.length) return;
    setSelectedRawMaterials((prev) => resolveIds(prev, allRawMaterials));
  }, [allRawMaterials, resolveIds]);

  useEffect(() => {
    if (!allDyes.length) return;
    setSelectedDyes((prev) => resolveIds(prev, allDyes));
  }, [allDyes, resolveIds]);

  useEffect(() => {
    if (!allPackaging.length) return;
    setSelectedPackaging((prev) => resolveIds(prev, allPackaging));
  }, [allPackaging, resolveIds]);

  useEffect(() => {
    if (!allFilaments.length) return;
    setSelectedFilaments((prev) => resolveIds(prev, allFilaments));
  }, [allFilaments, resolveIds]);

  useEffect(() => {
    if (!allConsumables.length) return;
    setSelectedConsumables((prev) => resolveIds(prev, allConsumables));
  }, [allConsumables, resolveIds]);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await loadOne();
      } catch (e) {
        console.error(e);
        alert('Не вдалося завантажити постачальника');
        navigate('/suppliers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, id, navigate, loadOne]);

  const toIds = (arr) => arr.filter(Boolean).map((m) => (typeof m === 'object' ? m.id : m));

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Вкажіть назву'); return; }
    const payload = {
      name: form.name.trim(),
      contact_name: (form.contact_name || '').trim(),
      phone: (form.phone || '').trim(),
      email: (form.email || '').trim(),
      address: form.address || '',
      notes: form.notes || '',
      is_active: form.is_active,
      raw_materials: toIds(selectedRawMaterials),
      dyes: toIds(selectedDyes),
      packaging_materials: toIds(selectedPackaging),
      filaments: toIds(selectedFilaments),
      consumable_materials: toIds(selectedConsumables),
    };
    try {
      setSaving(true);
      if (isNew) {
        const res = await supplierDirectoryService.create(payload);
        navigate(`/suppliers/${res.data.id}`, { replace: true });
        setSnackbar({ open: true, message: 'Збережено', severity: 'success' });
      } else {
        await supplierDirectoryService.update(id, payload);
        setSnackbar({ open: true, message: 'Збережено', severity: 'success' });
      }
    } catch (e) {
      console.error(e);
      const msg = e.response?.data
        ? (typeof e.response.data === 'string' ? e.response.data : JSON.stringify(e.response.data, null, 2))
        : e.message || 'Невідома помилка';
      setSnackbar({ open: true, message: `Помилка збереження: ${msg}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const makeMaterialTable = (label, options, selected, setSelected) => {
    const getId = (m) => (m == null ? null : typeof m === 'object' ? m.id : m);
    // Після збереження нового постачальника вибрані можуть бути ще голими id — назву беремо з довідника
    const getName = (m) => {
      if (m == null) return '';
      const obj = typeof m === 'object' ? m : options.find((o) => o.id === Number(m));
      return obj ? (obj.supplier_name || obj.name || '') : String(m);
    };
    const usedIds = new Set(selected.map(getId).filter((x) => x != null));
    const available = options.filter((o) => !usedIds.has(getId(o)));

    const addRow = () => {
      if (available.length === 0) return;
      setSelected((prev) => [...prev, null]);
    };
    const removeRow = (i) => setSelected((prev) => prev.filter((_, idx) => idx !== i));
    const changeRow = (i, opt) => setSelected((prev) => prev.map((v, idx) => (idx === i ? opt : v)));

    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addRow}>Додати</Button>
        </Stack>
        {selected.length === 0 ? (
          <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 2.5, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">Немає обраних — натисніть «Додати»</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Матеріал</TableCell>
                  <TableCell sx={{ width: 40, p: '4px !important' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {selected.map((item, i) => {
                  const itemId = item ? getId(item) : null;
                  const optionsForRow = options.filter(
                    (o) => getId(o) === itemId || !usedIds.has(getId(o)) || getId(o) === itemId
                  );
                  return (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={options.filter((o) => getId(o) === itemId || !selected.some((s, si) => si !== i && s && getId(s) === getId(o)))}
                          getOptionLabel={(o) => getName(o)}
                          value={item || null}
                          onChange={(_, v) => changeRow(i, v)}
                          isOptionEqualToValue={(o, v) => getId(o) === getId(v)}
                          renderInput={(params) => <TextField {...params} placeholder="Оберіть матеріал…" size="small" />}
                          sx={{ minWidth: 260 }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: '4px !important' }}>
                        <IconButton size="small" color="error" onClick={() => removeRow(i)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={6}>
        <CircularProgress />
        <Typography color="text.secondary">Завантаження…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pb: 10 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <PageBackHeader
            to="/suppliers"
            label="До списку постачальників"
            title={isNew ? 'Новий постачальник' : form.name || 'Картка постачальника'}
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
          }
          label={form.is_active ? 'Активний' : 'Неактивний'}
        />
      </Stack>

      <Stack spacing={3}>
        {/* Реквізити */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader icon={<BusinessIcon color="primary" fontSize="small" />} title="Реквізити" />
          <Stack spacing={2}>
            <TextField
              label="Назва"
              fullWidth
              required
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Ім'я контакту"
                fullWidth
                value={form.contact_name}
                onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))}
              />
              <TextField
                label="Телефон"
                fullWidth
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              />
              <TextField
                label="E-mail"
                fullWidth
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </Box>
            <TextField
              label="Адреса"
              fullWidth
              multiline
              minRows={2}
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
            />
          </Stack>
        </Paper>

        {/* Матеріали */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader icon={<CategoryIcon color="primary" fontSize="small" />} title="Матеріали від постачальника" />
          <Tabs
            value={matTab}
            onChange={(_, v) => setMatTab(v)}
            sx={{ mb: 3 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {MATERIAL_TABS.map((t) => <Tab key={t} label={t} />)}
          </Tabs>
          {matTab === 0 && (() => {
            const resolve = (m, all) => typeof m === 'object' ? m : (all.find((o) => o.id === m || o.id === Number(m)) || { id: m, name: String(m) });
            const allSelected = [
              ...selectedRawMaterials.map((m) => ({ ...resolve(m, allRawMaterials), category: 'Сировина' })),
              ...selectedDyes.map((m) => ({ ...resolve(m, allDyes), category: 'Барвники' })),
              ...selectedPackaging.map((m) => ({ ...resolve(m, allPackaging), category: 'Пакувальні матеріали' })),
              ...selectedFilaments.map((m) => ({ ...resolve(m, allFilaments), category: 'Філамент' })),
              ...selectedConsumables.map((m) => ({ ...resolve(m, allConsumables), category: 'Розхідні' })),
            ];
            if (allSelected.length === 0) {
              return (
                <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">Немає обраних матеріалів — оберіть у відповідній вкладці</Typography>
                </Box>
              );
            }
            return (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Категорія</TableCell>
                      <TableCell>Назва у постачальника</TableCell>
                      <TableCell>Назва</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allSelected.map((m, i) => (
                      <TableRow key={`${m.category}-${m.id ?? i}`} hover>
                        <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{m.category}</TableCell>
                        <TableCell>{m.supplier_name || '—'}</TableCell>
                        <TableCell>{m.name || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
          {matTab === 1 && makeMaterialTable('Сировина', allRawMaterials, selectedRawMaterials, setSelectedRawMaterials)}
          {matTab === 2 && makeMaterialTable('Барвники', allDyes, selectedDyes, setSelectedDyes)}
          {matTab === 3 && makeMaterialTable('Пакувальні матеріали', allPackaging, selectedPackaging, setSelectedPackaging)}
          {matTab === 4 && makeMaterialTable('Філамент', allFilaments, selectedFilaments, setSelectedFilaments)}
          {matTab === 5 && makeMaterialTable('Розхідні матеріали', allConsumables, selectedConsumables, setSelectedConsumables)}
        </Paper>

        {/* Примітки */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader icon={<NotesIcon color="primary" fontSize="small" />} title="Примітки" />
          <TextField
            label="Вільний текст"
            fullWidth
            multiline
            minRows={3}
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            helperText="Додаткова інформація, неструктуровані реквізити"
          />
        </Paper>
      </Stack>

      {/* Sticky bottom bar */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          mt: 3,
          py: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          zIndex: 1,
        }}
      >
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Збереження…' : 'Зберегти'}
        </Button>
        {!isNew && (
          <Button color="error" variant="outlined" onClick={() => setDeleteConfirmOpen(true)}>
            Видалити
          </Button>
        )}
      </Box>

      {/* Delete confirm dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Видалити постачальника?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Постачальника «{form.name}» буде видалено з довідника. Пов'язані закупівлі залишаться з текстовим
            полем «Постачальник» без прив'язки до довідника.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Скасувати
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              try {
                setDeleting(true);
                await supplierDirectoryService.delete(id);
                navigate('/suppliers');
              } catch (e) {
                console.error(e);
                setDeleteConfirmOpen(false);
                setSnackbar({ open: true, message: 'Не вдалося видалити', severity: 'error' });
              } finally {
                setDeleting(false);
              }
            }}
          >
            {deleting ? 'Видалення…' : 'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SupplierEditPage;
